import { ClipboardType, FileText, Globe, X } from "lucide-react";
import {
  describirMaterial,
  type MaterialConocimiento,
  type TipoMaterial,
} from "../datos/materialesConocimiento";
import { CLASE_FOCO, CLASE_GRILLA, CLASE_ROTULO, CLASE_TARJETA } from "./tarjetasFuente";

/**
 * El material cargado desde "Añadir fuente" que NO es un canal: archivos
 * anotados, páginas web y textos pegados.
 *
 * Usa la MISMA tarjeta y la misma grilla que las fuentes de arriba —mismo
 * ancho, misma media altura, mismo `truncate`— porque es material de la misma
 * base de conocimiento; lo que cambia es el ícono (genérico, no un logo de
 * marca) y, en los archivos, un sello que no está en ninguna otra tarjeta.
 *
 * EL SELLO "SIN SUBIR" NO ES DECORACIÓN. Es el tercer lugar donde la pantalla
 * dice que el archivo no se subió (los otros dos son el atajo del modal y la
 * confirmación previa a anotar), y el único que sigue estando una semana
 * después, cuando el usuario vuelve y ve su lista. Sin él, una lista de nombres
 * de archivo con una ✕ al lado se lee como "mis archivos están en Atlas", que
 * es exactamente lo contrario de lo que pasa. Ver
 * ADR-2026-08-17-estudio-marketing-modal-anadir-fuente.
 */

const ICONO: Record<TipoMaterial, typeof FileText> = {
  archivo: FileText,
  web: Globe,
  texto: ClipboardType,
};

/** Cómo se le dice a cada tipo en voz alta. `MarcoIcono` es decorativo
 *  (`aria-hidden`), así que sin esto un lector de pantalla no distingue un
 *  archivo de una página. */
const NOMBRE_TIPO: Record<TipoMaterial, string> = {
  archivo: "Archivo",
  web: "Página web",
  texto: "Texto pegado",
};

/** El mismo marco circular que `MarcoLogo`, con un ícono genérico adentro: acá
 *  no hay marca que mostrar. */
const MarcoIcono = ({ tipo }: { tipo: TipoMaterial }) => {
  const Icono = ICONO[tipo];
  return (
    <span
      aria-hidden="true"
      className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[rgb(var(--brand-black-rgb)_/_0.08)] bg-white text-[rgb(var(--brand-black-rgb)_/_0.55)]"
    >
      <Icono className="h-[18px] w-[18px]" />
    </span>
  );
};

const TarjetaMaterial = ({
  material,
  onQuitar,
}: {
  material: MaterialConocimiento;
  onQuitar: () => void;
}) => {
  const { titulo, detalle } = describirMaterial(material);
  return (
    <li className="flex">
      <div
        className={`${CLASE_TARJETA} border border-[rgb(var(--brand-black-rgb)_/_0.08)] bg-white`}
        // El detalle se trunca en una columna de un tercio; acá está entero.
        title={`${NOMBRE_TIPO[material.tipo]}: ${titulo} — ${detalle}`}
      >
        <MarcoIcono tipo={material.tipo} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[14px] font-semibold text-brand-black">{titulo}</p>
          <p className="truncate text-[12px] text-[rgb(var(--brand-black-rgb)_/_0.5)]">{detalle}</p>
        </div>
        {material.tipo === "archivo" && (
          <span
            className="shrink-0 rounded-full bg-[#F7EBD5] py-1 px-2.5 text-[11px] font-medium text-[#8A5A08]"
            title="El archivo no se subió a Atlas: quedó anotado sólo su nombre en este navegador."
          >
            Sin subir
          </span>
        )}
        <button
          type="button"
          onClick={onQuitar}
          aria-label={`Quitar ${titulo}`}
          className={`shrink-0 rounded-full p-1.5 text-[rgb(var(--brand-black-rgb)_/_0.4)] transition-colors hover:bg-[#F0F2F7] hover:text-[#C33B36] ${CLASE_FOCO}`}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </li>
  );
};

export const MaterialesCargados = ({
  materiales,
  onQuitar,
}: {
  materiales: MaterialConocimiento[];
  onQuitar: (id: string) => void;
}) => {
  if (materiales.length === 0) return null;
  const hayArchivos = materiales.some((material) => material.tipo === "archivo");
  return (
    <>
      <p className={`mt-6 ${CLASE_ROTULO}`}>Otro material ({materiales.length})</p>
      <ul className={CLASE_GRILLA}>
        {materiales.map((material) => (
          <TarjetaMaterial
            key={material.id}
            material={material}
            onQuitar={() => onQuitar(material.id)}
          />
        ))}
      </ul>
      {hayArchivos && (
        <p className="mt-2 text-[12px] leading-relaxed text-[#8A5A08]">
          De los archivos guardamos <strong className="font-semibold">sólo el nombre</strong>: el
          archivo en sí no se subió y sigue únicamente en tu computadora.
        </p>
      )}
    </>
  );
};

export default MaterialesCargados;
